import type { ApiClient } from "../auth/apiClient";
import {
    CloseConversationResponseSchema,
    TaskTypesResponseSchema,
    WorkflowKindsResponseSchema,
    WorkItemSchema,
    WorkflowListResponseSchema,
    WorkflowSchema,
    WorkflowSummarySchema,
} from "./types";
import type {
    CloseConversationResponse,
    TaskTypesResponse,
    WorkflowKindsResponse,
    WorkItem,
    Workflow,
    WorkflowListResponse,
    WorkflowSummary,
} from "./types";

export interface CreateWorkflowInput {
    readonly workflowId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly entityDisplayName: string;
    readonly kind: string;
    /**
     * Optional. The BFF derives a sensible default (first seed body,
     * else the entity display name) when this is blank, so dialogs
     * don't need to invent a throwaway title just to satisfy the
     * server contract.
     */
    readonly title?: string;
    readonly parentWorkflowId?: string | null;
    readonly parentWorkItemId?: string | null;
}

export async function fetchWorkflow(
    client: ApiClient,
    id: string,
    entityType: string,
    entityId: string,
): Promise<Workflow> {
    const params = new URLSearchParams({ entityType, entityId });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(id)}?${params.toString()}`,
    );

    if (!response.ok) {
        throw new Error(
            `Failed to fetch workflow ${id}: ${String(response.status)} ${response.statusText}`,
        );
    }

    const json: unknown = await response.json();
    return WorkflowSchema.parse(json);
}

export async function listWorkflowsByEntity(
    client: ApiClient,
    entityType: string,
    entityId: string,
): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({ entityType, entityId });
    const response = await client.fetch(`/workflows?${params.toString()}`);

    if (!response.ok) {
        throw new Error(
            `Failed to list workflows for ${entityType}:${entityId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowListResponseSchema.parse(json);
}

export async function createWorkflow(
    client: ApiClient,
    input: CreateWorkflowInput,
): Promise<WorkflowSummary> {
    const trimmedTitle = input.title?.trim() ?? "";
    const response = await client.fetch(`/workflows`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            workflowId: input.workflowId,
            entityType: input.entityType,
            entityId: input.entityId,
            entityDisplayName: input.entityDisplayName,
            kind: input.kind,
            // Omit the field entirely when blank so the BFF's "title is
            // optional" branch fires cleanly. Sending an empty string
            // also works server-side, but a missing key surfaces the
            // intent more honestly in logs and request inspectors.
            ...(trimmedTitle.length > 0 ? { title: trimmedTitle } : {}),
            parentWorkflowId: input.parentWorkflowId ?? null,
            parentWorkItemId: input.parentWorkItemId ?? null,
        }),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to create workflow ${input.workflowId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowSummarySchema.parse(json);
}

export interface AddWorkItemInput {
    readonly workflowId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly type: string;
    /**
     * Required when {@link type} is `"task"` and must be a value from
     * the published task-type vocabulary (`taskTypes.ts`); omitted for
     * messages and notes. The server validates membership at the
     * boundary and rejects unknown / mismatched values.
     */
    readonly taskType?: string;
    readonly body: string;
    readonly dueDate?: string;
    /**
     * When set, this item is a reply: it inherits the replied-to item's
     * queue distribution and bypasses the rule-driven router. When
     * omitted, the item enters the normal first-hop routing path.
     */
    readonly repliedToWorkItemId?: string;
    /**
     * When set, attaches a note to the work item it annotates. Permitted
     * only when {@link type} is `"note"`; the server rejects a parent link
     * on any other type. Omitted for a workflow-root note.
     */
    readonly parentWorkItemId?: string;
    /**
     * Optional explicit destination. When set, routing rules are
     * bypassed and the item is delivered to this queue. Omitted keeps
     * the rule-routed first hop.
     */
    readonly targetQueueId?: string;
}

export async function addWorkItem(
    client: ApiClient,
    input: AddWorkItemInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems?${params.toString()}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                type: input.type,
                ...(input.taskType !== undefined ? { taskType: input.taskType } : {}),
                body: input.body,
                ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
                ...(input.repliedToWorkItemId !== undefined
                    ? { repliedToWorkItemId: input.repliedToWorkItemId }
                    : {}),
                ...(input.parentWorkItemId !== undefined
                    ? { parentWorkItemId: input.parentWorkItemId }
                    : {}),
                ...(input.targetQueueId !== undefined
                    ? { targetQueueId: input.targetQueueId }
                    : {}),
            }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to add work item to workflow ${input.workflowId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export interface SpawnChildWorkflowInput {
    readonly parentWorkflowId: string;
    readonly parentWorkItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly originatingQueueId: string;
    readonly recipientQueueIds: readonly string[];
    readonly body: string;
    readonly title?: string;
}

export interface AskQuestionInput {
    readonly workflowId: string;
    readonly recipientQueueIds: readonly string[];
    readonly body: string;
    readonly title?: string;
    /**
     * Optional. When omitted the BFF resolves (and lazily provisions)
     * the asker's personal queue. Power users currently filtered into a
     * shared queue can supply that queue's id to attribute the question
     * to their team's inbox instead of their personal queue.
     */
    readonly originatingQueueId?: string;
    readonly dueDate?: string;
}

export async function askQuestion(
    client: ApiClient,
    input: AskQuestionInput,
): Promise<Workflow> {
    const trimmedTitle = input.title?.trim() ?? "";
    const response = await client.fetch(`/workflows/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            workflowId: input.workflowId,
            recipientQueueIds: input.recipientQueueIds,
            body: input.body,
            ...(trimmedTitle.length > 0 ? { title: trimmedTitle } : {}),
            ...(input.originatingQueueId !== undefined
                ? { originatingQueueId: input.originatingQueueId }
                : {}),
            ...(input.dueDate !== undefined ? { dueDate: input.dueDate } : {}),
        }),
    });

    if (!response.ok) {
        throw new Error(
            `Failed to ask question (workflow ${input.workflowId}): ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowSchema.parse(json);
}

export async function spawnChildWorkflow(
    client: ApiClient,
    input: SpawnChildWorkflowInput,
): Promise<Workflow> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.parentWorkflowId)}/workitems/${encodeURIComponent(input.parentWorkItemId)}/spawn-child?${params.toString()}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                originatingQueueId: input.originatingQueueId,
                recipientQueueIds: input.recipientQueueIds,
                body: input.body,
                ...(input.title !== undefined ? { title: input.title } : {}),
            }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to spawn child workflow from ${input.parentWorkflowId}/${input.parentWorkItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowSchema.parse(json);
}

export async function completeWorkItem(
    client: ApiClient,
    workflowId: string,
    workItemId: string,
    entityType: string,
    entityId: string,
): Promise<WorkItem> {
    const params = new URLSearchParams({ entityType, entityId });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(workflowId)}/workitems/${encodeURIComponent(workItemId)}/complete?${params.toString()}`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to complete work item ${workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

/**
 * Records that the current user opened a task or note for viewing. A
 * fire-and-forget read-path signal: the endpoint returns 204 with no
 * body, so there is nothing to parse. We still throw on a non-OK
 * response for consistency with the other api fns; the caller
 * (`useRecordWorkItemView`) deliberately swallows the rejection so a
 * failed view record never disrupts the UI.
 */
export async function recordWorkItemView(
    client: ApiClient,
    input: {
        readonly workflowId: string;
        readonly workItemId: string;
        readonly entityType: string;
        readonly entityId: string;
    },
): Promise<void> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/view?${params.toString()}`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to record view of work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
}

export interface SnoozeWorkItemInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    /**
     * Absolute ISO instant the due date should move to. The caller
     * computes the wall-clock target (alarm-clock model: quick chips
     * add their minutes to the client's `Date.now()`); the server
     * re-validates against its own clock so client-side skew can never
     * land a snooze in the past.
     */
    readonly newDueDate: string;
}

export async function snoozeWorkItem(
    client: ApiClient,
    input: SnoozeWorkItemInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/snooze?${params.toString()}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ newDueDate: input.newDueDate }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to snooze work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export async function setNotePinned(
    client: ApiClient,
    workflowId: string,
    workItemId: string,
    entityType: string,
    entityId: string,
    pinned: boolean,
): Promise<WorkItem> {
    const params = new URLSearchParams({ entityType, entityId });
    const action = pinned ? "pin" : "unpin";
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(workflowId)}/workitems/${encodeURIComponent(workItemId)}/${action}?${params.toString()}`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to ${action} note ${workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export interface EditNoteInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly body: string;
}

/**
 * Edits a note's body text. Note-only, queue-membership-scoped server
 * surface (PATCH `/workflows/{id}/notes/{itemId}`); the action is fully
 * audited with before/after on the activity ledger. Distinct from the
 * service-to-service body-correction endpoint under `/workitems/{itemId}`.
 */
export async function editNote(
    client: ApiClient,
    input: EditNoteInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/notes/${encodeURIComponent(input.workItemId)}?${params.toString()}`,
        {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ body: input.body }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to edit note ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export async function closeConversation(
    client: ApiClient,
    workflowId: string,
    entityType: string,
    entityId: string,
): Promise<CloseConversationResponse> {
    const params = new URLSearchParams({ entityType, entityId });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(workflowId)}/close-conversation?${params.toString()}`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to close conversation on workflow ${workflowId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return CloseConversationResponseSchema.parse(json);
}

export async function listChildWorkflows(
    client: ApiClient,
    workflowId: string,
    workItemId: string,
    entityType: string,
    entityId: string,
): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({ entityType, entityId });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(workflowId)}/workitems/${encodeURIComponent(workItemId)}/children?${params.toString()}`,
        { method: "GET" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to list child workflows for ${workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowListResponseSchema.parse(json);
}

export interface ApproveWorkItemInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
}

export async function approveWorkItem(
    client: ApiClient,
    input: ApproveWorkItemInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/approve?${params.toString()}`,
        { method: "POST" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to approve work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export interface RejectWorkItemInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly comment: string;
}

export async function rejectWorkItem(
    client: ApiClient,
    input: RejectWorkItemInput,
): Promise<WorkItem> {
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/reject`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                entityType: input.entityType,
                entityId: input.entityId,
                comment: input.comment,
            }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to reject work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export interface AttachWorkItemQueueInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly queueId: string;
}

export async function attachWorkItemQueue(
    client: ApiClient,
    input: AttachWorkItemQueueInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/queues?${params.toString()}`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ queueId: input.queueId }),
        },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to attach queue ${input.queueId} to work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export interface DetachWorkItemQueueInput {
    readonly workflowId: string;
    readonly workItemId: string;
    readonly entityType: string;
    readonly entityId: string;
    readonly queueId: string;
}

export async function detachWorkItemQueue(
    client: ApiClient,
    input: DetachWorkItemQueueInput,
): Promise<WorkItem> {
    const params = new URLSearchParams({
        entityType: input.entityType,
        entityId: input.entityId,
    });
    const response = await client.fetch(
        `/workflows/${encodeURIComponent(input.workflowId)}/workitems/${encodeURIComponent(input.workItemId)}/queues/${encodeURIComponent(input.queueId)}?${params.toString()}`,
        { method: "DELETE" },
    );

    if (!response.ok) {
        throw new Error(
            `Failed to detach queue ${input.queueId} from work item ${input.workItemId}: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkItemSchema.parse(json);
}

export async function searchWorkflows(
    client: ApiClient,
    query: string,
    skip?: number,
    take?: number,
): Promise<WorkflowListResponse> {
    const params = new URLSearchParams({ q: query });
    if (skip !== undefined) {
        params.set("skip", String(skip));
    }
    if (take !== undefined) {
        params.set("take", String(take));
    }

    const response = await client.fetch(`/workflows/search?${params.toString()}`);

    if (!response.ok) {
        throw new Error(
            `Failed to search workflows: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowListResponseSchema.parse(json);
}

export async function fetchWorkflowKinds(
    client: ApiClient,
): Promise<WorkflowKindsResponse> {
    const response = await client.fetch("/workflow-kinds");
    if (!response.ok) {
        throw new Error(
            `Failed to fetch workflow kinds: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return WorkflowKindsResponseSchema.parse(json);
}

export async function fetchTaskTypes(client: ApiClient): Promise<TaskTypesResponse> {
    const response = await client.fetch("/task-types");
    if (!response.ok) {
        throw new Error(
            `Failed to fetch task types: ${String(response.status)} ${response.statusText}`,
        );
    }
    const json: unknown = await response.json();
    return TaskTypesResponseSchema.parse(json);
}
